import { useState, type FormEvent } from 'react';
import { api, ApiError, type Me } from '../lib/api';
import { Alert, Button, Card, PasswordField, SectionTitle } from '../components/ui';
import { useEnter } from '../lib/motion';

/**
 * Kontoeinstellungen.
 *
 * Vorerst nur das Passwort. Bewusst eine eigene Seite und nicht ein Feld
 * irgendwo zwischen den Website-Einstellungen: es geht um das Konto, nicht um
 * eine einzelne Kundenwebsite.
 */
export function AccountPage({ me }: { me: Me }) {
  const [aktuell, setAktuell] = useState('');
  const [neu, setNeu] = useState('');
  const [wiederholung, setWiederholung] = useState('');
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [erfolg, setErfolg] = useState<string | null>(null);
  const ref = useEnter<HTMLDivElement>([]);

  const zuKurz = neu.length > 0 && neu.length < 12;
  const stimmtNicht = wiederholung.length > 0 && neu !== wiederholung;

  async function submit(event: FormEvent) {
    event.preventDefault();
    setFehler(null);
    setErfolg(null);

    if (neu !== wiederholung) {
      setFehler('Die beiden neuen Passwoerter stimmen nicht ueberein.');
      return;
    }

    setBusy(true);
    try {
      const antwort = await api.changePassword(aktuell, neu);
      setAktuell('');
      setNeu('');
      setWiederholung('');
      setErfolg(
        antwort.beendeteSitzungen > 0
          ? `Passwort geändert. ${antwort.beendeteSitzungen} andere ${
              antwort.beendeteSitzungen === 1 ? 'Sitzung wurde' : 'Sitzungen wurden'
            } beendet.`
          : 'Passwort geändert.',
      );
    } catch (caught) {
      setFehler(caught instanceof ApiError ? caught.message : 'Änderung fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div ref={ref} className="max-w-xl">
      <SectionTitle title="Konto" hint="Angemeldet als" />

      <Card className="mb-6">
        <dl className="grid gap-3 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-xs uppercase tracking-wide text-[var(--text-muted)]">E-Mail</dt>
            <dd className="tabular mt-0.5 font-medium">{me.user.email}</dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wide text-[var(--text-muted)]">Rolle</dt>
            <dd className="mt-0.5 font-medium">
              {me.user.platformRole === 'platform_admin' ? 'Plattform-Admin' : 'Konto'}
            </dd>
          </div>
        </dl>
      </Card>

      <Card>
        <SectionTitle
          title="Passwort ändern"
          hint="Nach der Änderung werden alle anderen Sitzungen beendet. Diese hier bleibt bestehen."
        />

        <form onSubmit={submit} className="flex flex-col gap-4">
          {fehler && <Alert tone="error">{fehler}</Alert>}
          {erfolg && <Alert tone="info">{erfolg}</Alert>}

          <PasswordField
            label="Aktuelles Passwort"
            name="current-password"
            autoComplete="current-password"
            required
            value={aktuell}
            onChange={(event) => setAktuell(event.target.value)}
          />
          <PasswordField
            label="Neues Passwort"
            name="new-password"
            autoComplete="new-password"
            required
            minLength={12}
            value={neu}
            onChange={(event) => setNeu(event.target.value)}
            hint="Mindestens 12 Zeichen. Nimm eines aus dem Passwortmanager."
            error={zuKurz ? `Noch ${12 - neu.length} Zeichen zu kurz.` : undefined}
          />
          <PasswordField
            label="Neues Passwort wiederholen"
            name="new-password-repeat"
            autoComplete="new-password"
            required
            value={wiederholung}
            onChange={(event) => setWiederholung(event.target.value)}
            error={stimmtNicht ? 'Stimmt nicht mit dem neuen Passwort ueberein.' : undefined}
          />

          <div>
            <Button type="submit" loading={busy} disabled={zuKurz || stimmtNicht || !aktuell || !neu}>
              Passwort ändern
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
